import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import {
  BILLING_BUYER,
  BILLING_CONSTANTS_SYMBOL,
  BILLING_DUE_DAYS,
  BILLING_ISSUE_OFFSET_DAYS,
  BILLING_SELLER,
  BILLING_UNIT_LABEL,
  BILLING_UNIT_PRICE_CZK,
  BILLING_VAT_RATE,
} from "@/config/billing";
import { getNextInvoiceNumber } from "@/billing/invoice-number";
import type { BillingBranchCapture } from "@/billing/use-billing-capture-counts";
import { DEJAVU } from "@/billing/fonts/dejavu";
import { DEJAVU_BOLD } from "@/billing/fonts/dejavu-bold";

const FONT_REGULAR = "DejaVuSans";
const FONT_BOLD = "DejaVuSansBold";
const FONT_REGULAR_FILE = "DejaVuSans.ttf";
const FONT_BOLD_FILE = "DejaVuSans-Bold.ttf";

export interface BillingLineItem {
  index: number;
  branchName: string;
  captureCount: number;
  unitPriceCzk: number;
  totalCzk: number;
}

export interface InvoiceTotals {
  subtotalCzk: number;
  vatCzk: number;
  totalCzk: number;
  roundingCzk: number;
  grandTotalCzk: number;
}

const CZECH_MONTHS = [
  "leden",
  "únor",
  "březen",
  "duben",
  "květen",
  "červen",
  "červenec",
  "srpen",
  "září",
  "říjen",
  "listopad",
  "prosinec",
] as const;

const formatCzechDate = (date: Date): string => {
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const yyyy = date.getFullYear();
  return `${dd}.${mm}.${yyyy}`;
};

const formatCzk = (value: number): string => {
  const sign = value < 0 ? "-" : "";
  const abs = Math.abs(value);
  const fixed = abs.toFixed(2);
  const [whole, fraction] = fixed.split(".");
  const wholeWithSpaces = whole.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  return `${sign}${wholeWithSpaces},${fraction}`;
};

const roundHalfUp = (value: number): number =>
  Math.sign(value) * Math.floor(Math.abs(value) + 0.5);

export const buildInvoiceLineItems = (
  captures: BillingBranchCapture[],
): BillingLineItem[] => {
  return captures
    .filter((entry) => entry.captureCount > 0)
    .sort((a, b) => a.branchName.localeCompare(b.branchName, "cs"))
    .map((entry, index) => {
      const totalCzk = entry.captureCount * BILLING_UNIT_PRICE_CZK;
      return {
        index: index + 1,
        branchName: entry.branchName,
        captureCount: entry.captureCount,
        unitPriceCzk: BILLING_UNIT_PRICE_CZK,
        totalCzk,
      };
    });
};

export const computeInvoiceTotals = (
  lineItems: BillingLineItem[],
): InvoiceTotals => {
  const subtotalExact = lineItems.reduce(
    (sum, item) => sum + item.captureCount * item.unitPriceCzk,
    0,
  );
  const totalCzk = roundHalfUp(subtotalExact);
  const vatCzk = roundHalfUp(totalCzk * BILLING_VAT_RATE);
  const grandTotalExact = totalCzk + vatCzk;
  const grandTotalCzk = roundHalfUp(grandTotalExact);
  const roundingCzk = +(grandTotalCzk - grandTotalExact).toFixed(2);
  return {
    subtotalCzk: totalCzk,
    vatCzk,
    totalCzk: grandTotalCzk,
    roundingCzk,
    grandTotalCzk,
  };
};

export interface InvoicePeriod {
  year: number;
  monthIndex: number;
}

export interface GeneratedInvoice {
  pdf: jsPDF;
  filename: string;
  invoiceNumber: string;
  variableSymbol: string;
  issueDate: Date;
  dueDate: Date;
  period: InvoicePeriod;
  lineItems: BillingLineItem[];
  totals: InvoiceTotals;
}

const registerFonts = (doc: jsPDF): void => {
  doc.addFileToVFS(FONT_REGULAR_FILE, DEJAVU);
  doc.addFont(FONT_REGULAR_FILE, FONT_REGULAR, "normal", undefined, "Identity-H");
  doc.addFileToVFS(FONT_BOLD_FILE, DEJAVU_BOLD);
  doc.addFont(FONT_BOLD_FILE, FONT_BOLD, "normal", undefined, "Identity-H");
};

const drawHeader = (
  doc: jsPDF,
  invoiceNumber: string,
): void => {
  doc.setFont(FONT_BOLD, "normal");
  doc.setFontSize(16);
  doc.setTextColor(0, 0, 0);
  doc.text("Algovectra s.r.o.", 14, 18);

  doc.setFontSize(12);
  doc.text(
    `FAKTURA - DAŇOVÝ DOKLAD č. ${invoiceNumber}`,
    210 - 14,
    18,
    { align: "right" },
  );

  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.2);
  doc.line(14, 22, 210 - 14, 22);
};

const drawSellerBlock = (doc: jsPDF, y: number): number => {
  doc.setFont(FONT_REGULAR, "normal");
  doc.setFontSize(9);
  doc.setTextColor(120, 120, 120);
  doc.text("Dodavatel:", 14, y);

  doc.setFont(FONT_BOLD, "normal");
  doc.setFontSize(10);
  doc.setTextColor(0, 0, 0);
  doc.text(BILLING_SELLER.name, 14, y + 6);
  doc.setFont(FONT_REGULAR, "normal");
  doc.text(BILLING_SELLER.street, 14, y + 11);
  doc.text(BILLING_SELLER.city, 14, y + 16);

  doc.setTextColor(120, 120, 120);
  doc.text("IČ:", 14, y + 24);
  doc.text("DIČ:", 14, y + 29);
  if (BILLING_SELLER.email) {
    doc.text("E-mail:", 14, y + 34);
  }

  doc.setTextColor(0, 0, 0);
  doc.text(BILLING_SELLER.ico, 35, y + 24);
  doc.text(BILLING_SELLER.dic, 35, y + 29);
  if (BILLING_SELLER.email) {
    doc.text(BILLING_SELLER.email, 35, y + 34);
  }

  return y + 40;
};

const drawInvoiceMeta = (
  doc: jsPDF,
  y: number,
  variableSymbol: string,
): number => {
  const rightX = 130;
  doc.setFont(FONT_REGULAR, "normal");
  doc.setFontSize(9);
  doc.setTextColor(120, 120, 120);
  doc.text("Variabilní symbol:", rightX, y);
  doc.text("Konstantní symbol:", rightX, y + 6);
  doc.text("Objednávka č.:", rightX, y + 12);
  doc.text("ze dne:", rightX + 50, y + 12);

  doc.setTextColor(0, 0, 0);
  doc.text(variableSymbol, 210 - 14, y, { align: "right" });
  doc.text(BILLING_CONSTANTS_SYMBOL, 210 - 14, y + 6, { align: "right" });
  doc.text("", 210 - 14, y + 12, { align: "right" });
  doc.text("", 210 - 14, y + 12, { align: "right" });

  return y + 18;
};

const drawBuyerBlock = (
  doc: jsPDF,
  buyerRect: { x: number; y: number; w: number; h: number },
): number => {
  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.4);
  doc.rect(buyerRect.x, buyerRect.y, buyerRect.w, buyerRect.h);

  const padX = buyerRect.x + 3;
  let cursorY = buyerRect.y + 6;

  doc.setFont(FONT_REGULAR, "normal");
  doc.setFontSize(9);
  doc.setTextColor(120, 120, 120);
  doc.text("Odběratel:", padX, cursorY);
  doc.text("IČ:", padX + 60, cursorY);
  doc.text("DIČ:", padX + 60, cursorY + 5);

  doc.setTextColor(0, 0, 0);
  doc.setFont(FONT_BOLD, "normal");
  doc.text(BILLING_BUYER.ico, 210 - 14 - 3, cursorY, { align: "right" });
  doc.setFont(FONT_REGULAR, "normal");
  doc.text(BILLING_BUYER.dic, 210 - 14 - 3, cursorY + 5, { align: "right" });

  cursorY += 12;
  doc.setFont(FONT_BOLD, "normal");
  doc.text(BILLING_BUYER.name, padX, cursorY);
  doc.text(BILLING_BUYER.street, padX, cursorY + 5);
  doc.text(BILLING_BUYER.city, padX, cursorY + 10);

  return buyerRect.y + buyerRect.h + 4;
};

const drawBankAndDates = (
  doc: jsPDF,
  y: number,
  issueDate: Date,
  dueDate: Date,
  supplyDate: Date,
): number => {
  doc.setFont(FONT_REGULAR, "normal");
  doc.setFontSize(9);
  doc.setTextColor(120, 120, 120);
  doc.text("Číslo účtu:", 14, y);

  doc.setTextColor(0, 0, 0);
  doc.setFont(FONT_BOLD, "normal");
  doc.setFontSize(11);
  doc.text(
    `${BILLING_SELLER.bankAccount} / ${BILLING_SELLER.bankCode}`,
    60,
    y + 4,
  );
  doc.setFont(FONT_REGULAR, "normal");
  doc.setFontSize(9);

  const dateY = y + 14;
  doc.setTextColor(120, 120, 120);
  doc.text("Datum vystavení:", 14, dateY);
  doc.text("Datum splatnosti:", 14, dateY + 6);
  doc.setTextColor(0, 0, 0);
  doc.text(formatCzechDate(issueDate), 60, dateY);
  doc.text(formatCzechDate(dueDate), 60, dateY + 6);

  doc.setTextColor(0, 0, 0);
  doc.setFont(FONT_BOLD, "normal");
  doc.text("Datum uskutečnění plnění:", 14, dateY + 12);
  doc.setFont(FONT_REGULAR, "normal");
  doc.text(formatCzechDate(supplyDate), 95, dateY + 12);

  doc.setTextColor(120, 120, 120);
  doc.text("Forma úhrady:", 14, dateY + 20);
  doc.setTextColor(0, 0, 0);
  doc.text("Příkazem", 60, dateY + 20);

  doc.setFont(FONT_REGULAR, "normal");
  doc.setFontSize(9);
  doc.setTextColor(120, 120, 120);
  doc.text("Konečný příjemce:", 130, dateY);

  return dateY + 24;
};

const drawLineItemsTable = (
  doc: jsPDF,
  startY: number,
  lineItems: BillingLineItem[],
  monthLabel: string,
  periodYear: number,
): { endY: number; vatSummary: Array<{ rate: number; base: number; vat: number; total: number }> } => {
  const headerRow = [
    "Označení dodávky",
    "Množství",
    "J.cena",
    "Sleva",
    "Cena %DPH",
    "DPH",
    "Kč Celkem",
  ];

  const descriptionRow = [
    {
      content: `Fakturujeme Vám provoz pizza checkeru za ${monthLabel} ${periodYear}:`,
      colSpan: 7,
      styles: { fontStyle: "bold", textColor: [0, 0, 0] as [number, number, number] },
    } as unknown as string,
  ];

  const bodyRows = lineItems.map((item) => [
    `${item.index}. ${item.branchName}`,
    `${item.captureCount.toLocaleString("cs-CZ")} ${BILLING_UNIT_LABEL}`,
    formatCzk(item.unitPriceCzk),
    "",
    {
      content: `${formatCzk(item.totalCzk)} ${Math.round(BILLING_VAT_RATE * 100)}%`,
      styles: { halign: "right" as const },
    },
    formatCzk(Math.round(item.totalCzk * BILLING_VAT_RATE * 100) / 100),
    formatCzk(item.totalCzk + Math.round(item.totalCzk * BILLING_VAT_RATE * 100) / 100),
  ]);

  autoTable(doc, {
    startY,
    head: [headerRow],
    body: [descriptionRow, ...bodyRows],
    theme: "grid",
    styles: {
      fontSize: 9,
      cellPadding: 2,
      lineColor: [0, 0, 0],
      lineWidth: 0.1,
      textColor: [0, 0, 0],
    },
    headStyles: {
      fillColor: [255, 255, 255],
      textColor: [0, 0, 0],
      fontStyle: "normal",
      lineWidth: 0.1,
      lineColor: [0, 0, 0],
    },
    bodyStyles: {
      fillColor: [255, 255, 255],
    },
    columnStyles: {
      0: { cellWidth: 55, halign: "left" },
      1: { cellWidth: 17, halign: "right" },
      2: { cellWidth: 16, halign: "right" },
      3: { cellWidth: 12, halign: "right" },
      4: { cellWidth: 25, halign: "right" },
      5: { cellWidth: 21, halign: "right" },
      6: { cellWidth: 24, halign: "right" },
    },
    margin: { left: 14, right: 14 },
  });

  const finalY =
    (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable
      ?.finalY ?? startY;

  const vatSummary = [
    {
      rate: 0,
      base: 0.38,
      vat: 0,
      total: 0,
    },
    {
      rate: 0.12,
      base: 0,
      vat: 0,
      total: 0,
    },
    {
      rate: BILLING_VAT_RATE,
      base: lineItems.reduce(
        (sum, item) => sum + item.captureCount * item.unitPriceCzk,
        0,
      ),
      vat: lineItems.reduce(
        (sum, item) =>
          sum + Math.round(item.captureCount * item.unitPriceCzk * BILLING_VAT_RATE * 100) / 100,
        0,
      ),
      total: 0,
    },
  ];
  vatSummary[2].total = +(vatSummary[2].base + vatSummary[2].vat).toFixed(2);

  return { endY: finalY, vatSummary };
};

const drawTotalsBox = (
  doc: jsPDF,
  startY: number,
  lineItems: BillingLineItem[],
  totals: InvoiceTotals,
): number => {
  const subtotalBase = lineItems.reduce(
    (sum, item) => sum + item.captureCount * item.unitPriceCzk,
    0,
  );
  const vat = lineItems.reduce(
    (sum, item) =>
      sum + Math.round(item.captureCount * item.unitPriceCzk * BILLING_VAT_RATE * 100) / 100,
    0,
  );
  const totalWithVat = subtotalBase + vat;

  const rows: string[][] = [
    ["Součet položek", formatCzk(subtotalBase), formatCzk(vat), formatCzk(totalWithVat)],
    ["Zaokrouhlení", "", "", formatCzk(totals.roundingCzk)],
  ];

  autoTable(doc, {
    startY,
    body: rows,
    theme: "grid",
    styles: {
      fontSize: 9,
      cellPadding: 2,
      lineColor: [0, 0, 0],
      lineWidth: 0.1,
      textColor: [0, 0, 0],
    },
    columnStyles: {
      0: { cellWidth: 80, halign: "left" },
      1: { cellWidth: 28, halign: "right" },
      2: { cellWidth: 28, halign: "right" },
      3: { cellWidth: 28, halign: "right" },
    },
    margin: { left: 14, right: 14 },
  });

  const finalY =
    (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable
      ?.finalY ?? startY;

  doc.setFont(FONT_BOLD, "normal");
  doc.setFontSize(11);
  doc.setTextColor(0, 0, 0);
  doc.text("CELKEM K ÚHRADĚ", 14, finalY + 8);
  doc.text(formatCzk(totals.grandTotalCzk), 210 - 14, finalY + 8, {
    align: "right",
  });

  return finalY + 14;
};

const drawVatRekapitulace = (
  doc: jsPDF,
  startY: number,
  lineItems: BillingLineItem[],
): number => {
  const subtotalBase = lineItems.reduce(
    (sum, item) => sum + item.captureCount * item.unitPriceCzk,
    0,
  );
  const vat = lineItems.reduce(
    (sum, item) =>
      sum + Math.round(item.captureCount * item.unitPriceCzk * BILLING_VAT_RATE * 100) / 100,
    0,
  );

  doc.setFont(FONT_REGULAR, "normal");
  doc.setFontSize(9);
  doc.setTextColor(0, 0, 0);
  doc.text("Rekapitulace DPH v Kč:", 14, startY);

  const headerRow: string[] = ["", "Základ v Kč", "Sazba", "DPH v Kč", "Celkem s DPH v Kč"];
  const bodyRows: string[][] = [
    ["", formatCzk(0.38), "0%", formatCzk(0), formatCzk(0)],
    ["", formatCzk(0), "12%", formatCzk(0), formatCzk(0)],
    [
      "",
      formatCzk(subtotalBase),
      `${Math.round(BILLING_VAT_RATE * 100)}%`,
      formatCzk(vat),
      formatCzk(subtotalBase + vat),
    ],
  ];

  autoTable(doc, {
    startY: startY + 3,
    head: [headerRow],
    body: bodyRows,
    theme: "grid",
    styles: {
      fontSize: 9,
      cellPadding: 2,
      lineColor: [0, 0, 0],
      lineWidth: 0.1,
      textColor: [0, 0, 0],
    },
    headStyles: {
      fillColor: [255, 255, 255],
      textColor: [0, 0, 0],
      fontStyle: "normal",
    },
    columnStyles: {
      0: { cellWidth: 32, halign: "left" },
      1: { cellWidth: 26, halign: "right" },
      2: { cellWidth: 16, halign: "right" },
      3: { cellWidth: 26, halign: "right" },
      4: { cellWidth: 26, halign: "right" },
    },
    margin: { left: 70, right: 14 },
  });

  const finalY =
    (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable
      ?.finalY ?? startY;

  return finalY + 6;
};

const drawSignatures = (doc: jsPDF, y: number): number => {
  doc.setFont(FONT_REGULAR, "normal");
  doc.setFontSize(9);
  doc.setTextColor(0, 0, 0);
  doc.text("Vystavil: Bc. Rončková Martina", 14, y);
  doc.text("Převzal:", 14, y + 18);
  doc.text("Razítko:", 130, y + 18);
  doc.setFontSize(7);
  doc.setTextColor(120, 120, 120);
  doc.text("Ekonomický a informační systém POHODA", 14, 280);
  return y + 24;
};

const drawRegistrationFooter = (doc: jsPDF): void => {
  doc.setFont(FONT_REGULAR, "normal");
  doc.setFontSize(8);
  doc.setTextColor(80, 80, 80);
  doc.text("C 145932/KSBR Krajský soud v Brně", 14, 275);
};

export const generateInvoice = (
  captures: BillingBranchCapture[],
  period: InvoicePeriod,
): GeneratedInvoice => {
  const today = new Date();
  const issueDate = new Date(today);
  issueDate.setDate(issueDate.getDate() + BILLING_ISSUE_OFFSET_DAYS);
  const dueDate = new Date(issueDate);
  dueDate.setDate(dueDate.getDate() + BILLING_DUE_DAYS);
  const supplyDate = new Date(
    Date.UTC(period.year, period.monthIndex + 1, 0),
  );

  const lineItems = buildInvoiceLineItems(captures);
  const totals = computeInvoiceTotals(lineItems);

  const invoiceNumber = getNextInvoiceNumber(period.year, period.monthIndex);

  const doc = new jsPDF({
    unit: "mm",
    format: "a4",
    orientation: "portrait",
  });

  registerFonts(doc);
  doc.setFont(FONT_REGULAR, "normal");

  drawHeader(doc, invoiceNumber.short);

  let cursorY = drawSellerBlock(doc, 28);
  cursorY = drawInvoiceMeta(doc, 28, invoiceNumber.variableSymbol);

  const buyerRect = { x: 130, y: 46, w: 210 - 14 - 130, h: 36 };
  cursorY = drawBuyerBlock(doc, buyerRect);

  cursorY = drawBankAndDates(doc, cursorY, issueDate, dueDate, supplyDate);

  const monthLabel = CZECH_MONTHS[period.monthIndex];
  const { endY: tableEndY } = drawLineItemsTable(
    doc,
    cursorY + 4,
    lineItems,
    monthLabel,
    period.year,
  );

  const totalsEndY = drawTotalsBox(doc, tableEndY + 4, lineItems, totals);

  const vatEndY = drawVatRekapitulace(doc, totalsEndY + 6, lineItems);

  drawSignatures(doc, vatEndY + 4);
  drawRegistrationFooter(doc);

  return {
    pdf: doc,
    filename: invoiceNumber.filename,
    invoiceNumber: invoiceNumber.short,
    variableSymbol: invoiceNumber.variableSymbol,
    issueDate,
    dueDate,
    period,
    lineItems,
    totals,
  };
};

export const downloadInvoice = (generated: GeneratedInvoice): void => {
  generated.pdf.save(generated.filename);
};